import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase } from '../shared.jsx';

function TermsAcceptanceView({ conflict, user, onBack, onUpdate }) {
  const toast = useToast();
  const [accepting, setAccepting] = useState(false);
  const [proposingChanges, setProposingChanges] = useState(false);
  const [proposedHours, setProposedHours] = useState(conflict.responseHours);
  const [proposedWaitTime, setProposedWaitTime] = useState(conflict.responseWaitTime);
  const [proposedWindow, setProposedWindow] = useState(conflict.responseWindow);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      // Re-read conflict from Firebase to avoid overwriting other users' changes
      const freshConflict = await storage.get(`conflict:${conflict.id}`, true) || conflict;

      const updated = {
        ...freshConflict,
        termsAcceptance: {
          ...freshConflict.termsAcceptance,
          acceptedBy: [...(freshConflict.termsAcceptance.acceptedBy || []), user.id]
        },
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };

      // Ensure this user's mentee entry has their real ID, name, and avatar (only replace placeholder IDs)
      const menteeIndex = updated.mentees.findIndex(
        m => m.email?.toLowerCase() === user.email?.toLowerCase()
      );
      if (menteeIndex !== -1 && !updated.mentees[menteeIndex].id?.startsWith('user-') && updated.mentees[menteeIndex].id !== user.id) {
        updated.mentees[menteeIndex] = {
          ...updated.mentees[menteeIndex],
          id: user.id,
          name: user.name,
          avatar: user.avatar
        };
      }

      // Check if all non-creator mentees have accepted (creator is auto-accepted at creation)
      const nonCreatorMentees = updated.mentees.filter(m => m.id !== updated.createdBy);
      const nonCreatorIds = nonCreatorMentees.map(m => m.id).filter(id => id);
      const allMenteesAccepted = nonCreatorIds.every(id =>
        updated.termsAcceptance.acceptedBy.includes(id)
      );

      if (allMenteesAccepted || updated.mentees.length === 1) {
        updated.termsAcceptance.status = 'accepted';
        updated.status = 'identify-define';
        updated.statusHistory.push({ 
          status: 'identify-define', 
          timestamp: new Date().toISOString() 
        });
      }

      addAuditEntry(updated, user.id, user.name, 'Accepted terms', null, user.email);
      await storage.set(`conflict:${conflict.id}`, updated, true);
      onUpdate();
    } catch (error) {
      console.error('Error accepting terms:', error);
      toast('Failed to accept terms. Please try again.');
    }
    setAccepting(false);
  };

  const handleProposeChanges = async () => {
    setAccepting(true);
    try {
      // Re-read conflict from Firebase to avoid overwriting other users' changes
      const freshConflict = await storage.get(`conflict:${conflict.id}`, true) || conflict;

      const updated = {
        ...freshConflict,
        termsAcceptance: {
          ...freshConflict.termsAcceptance,
          proposedChanges: {
            proposedBy: user.id,
            proposedByName: user.name,
            responseHours: proposedHours,
            responseWaitTime: proposedWaitTime,
            responseWindow: proposedWindow,
            timestamp: new Date().toISOString()
          },
          status: 'changes-proposed'
        },
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };

      addAuditEntry(updated, user.id, user.name, 'Proposed changes to terms', `Response hours: ${proposedHours}, Wait: ${proposedWaitTime}min, Window: ${proposedWindow}min`, user.email);
      await storage.set(`conflict:${conflict.id}`, updated, true);
      onUpdate();
    } catch (error) {
      console.error('Error proposing changes:', error);
      toast('Failed to propose changes. Please try again.');
    }
    setAccepting(false);
  };

  return (
    <div className="min-h-screen p-3 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-stone-600 hover:text-stone-800 mb-6 transition-colors"
        >
          ← Back to Dashboard
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8 border border-stone-200">
          <div className="text-center mb-6 sm:mb-8">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-amber-700" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-light text-stone-800 mb-2">Terms Acceptance</h2>
            <p className="text-stone-600">Please review and accept the conflict resolution terms</p>
          </div>

          {/* Conflict Info */}
          <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-stone-50 rounded-xl">
            <h3 className="font-medium text-stone-800 mb-4">Conflict: {conflict.title}</h3>
            <p className="text-stone-600 mb-4">{conflict.problemStatement?.what || 'No description'}</p>
            <div className="text-sm text-stone-500">
              Created by: {conflict.mentees?.find(m => m.id === conflict.createdBy)?.name || 'Conflict creator'}
            </div>
          </div>

          {/* Current Terms */}
          <div className="mb-6 sm:mb-8">
            <h3 className="font-medium text-stone-800 mb-4">Proposed Response Settings</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-stone-50 rounded-lg">
                <div className="text-sm text-stone-500 mb-1">Response Hours</div>
                <div className="text-lg font-medium text-stone-800">{conflict.responseHours}</div>
              </div>
              <div className="p-4 bg-stone-50 rounded-lg">
                <div className="text-sm text-stone-500 mb-1">Wait Time</div>
                <div className="text-lg font-medium text-stone-800">{conflict.responseWaitTime} min</div>
              </div>
              <div className="p-4 bg-stone-50 rounded-lg">
                <div className="text-sm text-stone-500 mb-1">Response Window</div>
                <div className="text-lg font-medium text-stone-800">{conflict.responseWindow} min</div>
              </div>
            </div>
          </div>

          {!proposingChanges ? (
            <div className="space-y-4">
              <button
                onClick={handleAccept}
                disabled={accepting}
                className="w-full bg-green-700 hover:bg-green-800 disabled:bg-green-400 text-white py-4 rounded-lg font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {accepting ? 'Accepting...' : 'Accept These Terms'}
              </button>
              <button
                onClick={() => setProposingChanges(true)}
                className="w-full bg-amber-700 hover:bg-amber-800 text-white py-4 rounded-lg font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                Propose Different Terms
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <h3 className="font-medium text-stone-800">Propose Your Preferred Settings</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Response Hours
                  </label>
                  <select
                    value={proposedHours}
                    onChange={(e) => setProposedHours(e.target.value)}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="9-5">9am - 5pm</option>
                    <option value="5-9">5pm - 9pm</option>
                    <option value="24">24 hours</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Wait Time (min)
                  </label>
                  <select
                    value={proposedWaitTime}
                    onChange={(e) => setProposedWaitTime(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value={5}>5 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Window (min)
                  </label>
                  <select
                    value={proposedWindow}
                    onChange={(e) => setProposedWindow(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value={5}>5 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleProposeChanges}
                  disabled={accepting}
                  className="flex-1 bg-amber-700 hover:bg-amber-800 disabled:bg-amber-400 text-white py-3 rounded-lg font-medium transition-colors"
                >
                  {accepting ? 'Proposing...' : 'Submit Proposal'}
                </button>
                <button
                  onClick={() => setProposingChanges(false)}
                  className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-700 py-3 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Conflict Detail View Component

export default TermsAcceptanceView;
